//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, Col, Container, Row, Button, Table, Pagination, Form } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, usePagination } from "react-table";
import { UserAccountsActions } from "./userAccountsActions";
import { Context } from "../common/context/Context";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";
import { CandidatePageActions } from "../candidatePageActions";
import { getAccessTypes } from "../aAdminTabTeam2/hooksAndFunctions/functions/getAccessTypes";

const UserAccounts = () => {

    const { isEnterpriseAdmin,
        clientPortalAdminType,
        candidatePortalAdminType,
    } = getAccessTypes()

    const {
        isClientPortalAdmin,
        hasA1Access,
        hasA2Access,
        hasA3Access
    } = clientPortalAdminType

    const [userAccountsJson, setUserAccountsJson] = useState({});
    const navigate = useNavigate();
    const [usersList, setUsersList] = useState([]);
    const context = useContext(Context)
    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })
    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    const fetchUsers = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        await UserAccountsActions.GetAllUsers().then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    setUsersList(res?.data);
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const fetchUserAccountsJson = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        await UserAccountsActions.GetUserAccountsJSON().then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    setUserAccountsJson(JSON.parse(JSON.stringify(res?.data)));
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }
    const fetchUserAccountsLabel = (key) => {
        let refJsonContent = JSON.parse(JSON.stringify(userAccountsJson));
        let label = "";

        refJsonContent?.Properties?.Labels?.map((x) => {
            if (x.Key === key) {
                label = x.Phrase;
            }
        });

        return label;
    };

    const getCountriesList = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        let locale = "en-US";
        let key = "country";
        let selectAllLabel = "Select All";
        let selectAllValue = 'all';
        let selectAllOption = { label: selectAllLabel, value: selectAllValue }
        CandidatePageActions.GetCountriesList(locale, key).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    let countryLists = [];
                    res?.data[0]?.items?.forEach((item) => {
                        let country = {}
                        country.title = item.title;
                        item?.standardCodes?.forEach((subItem) => {
                            if (subItem?.itemCodeStandardName === 'rt') {
                                country.standardCodeValue = subItem?.standardCodeValue
                                countryLists.push(country);
                            }
                        })
                    })
                    context.setCountryList(countryLists)
                    let countryOptions = countryLists?.map((item) => ({ label: item?.title, value: item?.standardCodeValue }))
                    context.setUserRoleCountryList([selectAllOption, ...countryOptions]);
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    useEffect(() => {
        fetchUserAccountsJson();
        fetchUsers();
        getCountriesList();
    }, []);

    function DefaultColumnFilter({
        column: { filterValue, preFilteredRows, setFilter },
    }) {
        const count = preFilteredRows.length;

        return (
            <Form.Control
                value={filterValue || ""}
                onChange={(e) => {
                    setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
                }}
                placeholder={`Search ${count} records...`}
                className="mt-2"
            />
        );
    }
    const ManageAccounts = ({ columns, data }) => {
        const filterTypes = React.useMemo(
            () => ({
                // Or, override the default text filter to use
                // "startWith"
                text: (rows, id, filterValue) => {
                    return rows.filter((row) => {
                        const rowValue = row.values[id];
                        return rowValue !== undefined
                            ? String(rowValue)
                                .toLowerCase()
                                .startsWith(String(filterValue).toLowerCase())
                            : true;
                    });
                },
            }),
            []
        );

        const defaultColumn = React.useMemo(
            () => ({
                // Let's set up our default Filter UI
                Filter: DefaultColumnFilter,
            }),
            []
        );
        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            prepareRow,
            page,
            canPreviousPage,
            canNextPage,
            pageOptions,
            pageCount,
            gotoPage,
            nextPage,
            previousPage,
            setPageSize,
            state: { pageIndex, pageSize },
        } = useTable(
            {
                columns,
                data,
                defaultColumn, // Be sure to pass the defaultColumn option
                filterTypes,
                initialState: { pageIndex: 0 },
            },
            useFilters, // useFilters!
            useGlobalFilter,// useGlobalFilter!
            usePagination
        );
        return (
            <Container fluid className="p-0 m-3">
                {errorAlert?.showAlert &&
                    <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                }
                <Card>
                    <Card.Header>
                        <Card.Title className="mb-0"> {fetchUserAccountsLabel('UserAccounts.ManageAccounts')}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <h6>{fetchUserAccountsLabel('UserAccounts.TotalNumberofResults')} {rows.length}</h6>
                        <Table className="mb-0" {...getTableProps()} responsive>
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <th {...column.getHeaderProps()}>
                                                {column.render("Header")}
                                                <div>
                                                    {column.canFilter ? column.render("Filter") : null}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {page.map((row, i) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map((cell) => {
                                                return (
                                                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        <Row className="mt-3">
                            <Col md="6" className="d-flex flex-lg-row flex-column">
                                <div className="me-2 mt-1">
                                    {fetchUserAccountsLabel('UserAccounts.Page')}{" "}
                                    <strong>
                                        {pageIndex + 1} of {pageOptions.length}
                                    </strong>
                                </div>
                                <div className="me-2">
                                    <span className="me-2">{fetchUserAccountsLabel('UserAccounts.Show')}</span>
                                    <Form.Select
                                        className="d-inline-block w-auto"
                                        value={pageSize}
                                        aria-label="pageNumber"
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                        }}
                                    >
                                        {[10, 20, 30, 40, 50].map((pageSize) => (
                                            <option key={pageSize} value={pageSize}>
                                                {pageSize}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="me-2">
                                    <span className="me-2">{fetchUserAccountsLabel('UserAccounts.GoToPage')}</span>
                                    <Form.Control
                                        className="d-inline-block"
                                        type="number"
                                        aria-label="number"
                                        min="1"
                                        defaultValue={pageIndex + 1}
                                        onChange={(e) => {
                                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                            gotoPage(page);
                                        }}
                                        style={{ width: "75px" }}
                                    />
                                </div>
                            </Col>
                            <Col md="6" className="mt-1">
                                <Pagination className="float-lg-end float-md-end float-left">
                                    <Pagination.First
                                        onClick={() => gotoPage(0)}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Prev
                                        onClick={() => previousPage()}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Next
                                        onClick={() => nextPage()}
                                        disabled={!canNextPage}
                                    />
                                    <Pagination.Last
                                        onClick={() => gotoPage(pageCount - 1)}
                                        disabled={!canNextPage}
                                    />
                                </Pagination>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    const accountTableColumns = [
        {
            id: "UserAccounts.Name",
            Header: fetchUserAccountsLabel('UserAccounts.Name'),
            accessor: (values) => {
                const { firstName, lastName } = values;
                return lastName + ',' + firstName;
            },
            Cell: ({ row, value }) =>
            (
                // context?.permissionsList && context?.permissionsList.includes("Generic Account.viewgenericaccount") ?
                <Link to={'/user-details'}
                    state={{ firstName: row?.original?.firstName, lastName: row?.original?.lastName, emailAddress: row?.original?.emailAddress, staffId: row?.original?.staffId, staffRole: row?.original?.staffRole }}>
                    {value}
                </Link>
                // : value
            )
        },
        {
            id: "UserAccounts.UserRole",
            Header: fetchUserAccountsLabel('UserAccounts.UserRole'),
            accessor: "staffRole",
            Filter: false
        },
        {
            id: "UserAccounts.Email",
            Header: fetchUserAccountsLabel('UserAccounts.Email'),
            accessor: "emailAddress",

        },
        {
            id: "UserAccounts.Phone",
            Header: fetchUserAccountsLabel('UserAccounts.Phone'),
            accessor: "primaryContactNo",
            Filter: false
        },
        {
            id: "UserAccounts.ServiceOffice",
            Header: fetchUserAccountsLabel('UserAccounts.ServiceOffice'),
            accessor: "rightOffice",
            Filter: false
        },
    ];

    return (
        <React.Fragment>
            <Col lg='12'>
                <Container fluid className="p-0">
                    <h1 className="h3 m-3">{fetchUserAccountsLabel('UserAccounts.UserAccountsHeader')}</h1>
                    <Row>
                        {((context.permissionsList && context.permissionsList.includes("Users.createusers")) || !!hasA1Access) &&
                            <Col>
                                <Card>
                                    <Card.Header>
                                        <Card.Title className="mb-0">{fetchUserAccountsLabel('UserAccounts.NewUser')}</Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        <Card.Text>
                                            {fetchUserAccountsLabel('UserAccounts.SetUpNewUser')}
                                        </Card.Text>
                                        <Button
                                            variant="primary"
                                            onClick={() => { navigate("/createNewUser") }}
                                        >
                                            {fetchUserAccountsLabel('UserAccounts.CreateNewUser')}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        }
                    </Row>
                    <Row>
                        <ManageAccounts columns={accountTableColumns} data={usersList} />
                    </Row>
                </Container>
            </Col>
        </React.Fragment>
    )
}
export default UserAccounts
